import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Flex, Grid, Text, Button, Checkbox, Spinner, Icon, useToast,
} from '@chakra-ui/react';
import { FiChevronLeft } from 'react-icons/fi';
import dayjs from 'dayjs';
import {
  getTest, publishTest, discardTest, TEST_PHASE, reasonLabel, formatScore, errorMessage,
} from 'api/administrador/monitoring/aiGrading';
import { KpiCard, ChipGroup, StatusPill } from './common';
import ConfirmActionModal from './ConfirmActionModal';

const PAGE = 20;

function resultOf(item, comparing) {
  if (item.outcome === 'graded') {
    if (comparing) return { label: 'Calificada', tone: 'green' };
    if (item.approved) return { label: 'Por publicar', tone: 'blue' };
    if (item.gradedByTeacherSince) return { label: 'La calificó el docente', tone: 'purple' };
    if (item.flag) return { label: 'Para revisar', tone: 'orange' };
    return { label: 'Calificada', tone: 'green' };
  }
  if (item.outcome === 'deferred') return { label: 'Al docente', tone: 'gray' };
  return { label: 'Error', tone: 'red' };
}

export default function TestResult({ testId, onBack, onChanged }) {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(new Set());
  const [shown, setShown] = useState(PAGE);
  const [modal, setModal] = useState(null);

  const load = useCallback(async () => {
    try {
      const d = await getTest(testId, { filter: 'all' });
      setData(d);
      // Preselección: lo publicable que no está marcado para revisar.
      setSelected(new Set(d.items.filter((i) => i.publishable && !i.flag).map((i) => i.sittingId)));
    } catch (e) {
      toast({ status: 'error', title: errorMessage(e) });
    }
  }, [testId, toast]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (data?.phase !== 'running') return undefined;
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [data?.phase, load]);

  const comparing = data?.filters.mode === 'graded';
  const items = useMemo(() => (data?.items || []).filter((r) => filter === 'all'
    || (filter === 'review' && r.flag)
    || (filter === 'teacher' && (r.outcome === 'deferred' || r.outcome === 'error'))), [data, filter]);

  if (!data) return <Flex justify="center" py={16}><Spinner color="#574feb" /></Flex>;

  const s = data.summary;
  const phase = TEST_PHASE[data.phase] || TEST_PHASE.running;
  const subjects = (data.filters.subjectNames || []).join(', ');
  const cols = comparing ? '2fr 0.9fr 3fr 1fr' : '0.3fr 2fr 0.6fr 3fr 1.1fr';
  const canPublish = !comparing && (data.phase === 'review' || data.phase === 'published');
  const toggle = (id, on) => setSelected((prev) => {
    const next = new Set(prev);
    if (on) next.add(id); else next.delete(id);
    return next;
  });

  return (
    <Box>
      <Button variant="link" leftIcon={<Icon as={FiChevronLeft} />} color="#574feb" fontSize="14px" mb={3} onClick={onBack}>Pruebas</Button>
      <Flex justify="space-between" align="flex-end" gap={4} mb={4} wrap="wrap">
        <Box>
          <Text fontSize="22px" fontWeight="700">#{data.id} · {subjects}</Text>
          <Text fontSize="14px" color="gray.600">
            {comparing ? 'Comparación sobre entregas ya calificadas' : 'Prueba sobre entregas pendientes'} · {data.progress.total} entregas
            · {dayjs(data.startedAt).format('DD MMM HH:mm')}
            {data.finishedAt ? ` · ${Math.max(1, Math.round((new Date(data.finishedAt) - new Date(data.startedAt)) / 1000))} s` : ''}
            {` · ≈ $${(data.progress.total * data.costPerSitting).toFixed(3).replace('.', ',')}`}
          </Text>
        </Box>
        <StatusPill label={phase.label} tone={phase.tone} />
      </Flex>

      {data.phase === 'running' && (
        <Box mb={5}>
          <Box h="8px" borderRadius="999px" bg="gray.100"><Box h="8px" borderRadius="999px" bg="#574feb" w={`${Math.round((data.progress.done / (data.progress.total || 1)) * 100)}%`} /></Box>
          <Text fontSize="13px" color="gray.600" mt={1.5}>{data.progress.done} de {data.progress.total} calificadas…</Text>
        </Box>
      )}

      <Grid templateColumns={{ base: 'repeat(2, 1fr)', md: 'repeat(4, minmax(0, 1fr))' }} gap={3} mb={5}>
        <KpiCard label="Calificadas" value={s.graded} foot={`de ${s.items}`} color="#10B981" />
        <KpiCard label="Al docente" value={s.deferred + s.errors} foot={s.errors ? `${s.errors} con error` : 'no se pudieron calificar'} color="#F59E0B" />
        {comparing ? (
          <>
            <KpiCard label="Nota IA / docente" value={`${formatScore(s.avgScore)} / ${formatScore(s.teacherAvg)}`} foot={s.bias !== undefined ? `la IA ${s.bias < 0 ? 'baja' : 'sube'} ${formatScore(Math.abs(s.bias))} en promedio` : ''} color="#8B5CF6" />
            <KpiCard label="Diferencia prom." value={formatScore(s.mae)} foot={s.within2 !== undefined ? `${s.within2} % a ±2 puntos` : ''} color="#574feb" />
          </>
        ) : (
          <>
            <KpiCard label="Nota promedio" value={formatScore(s.avgScore)} foot="de las calificadas" color="#8B5CF6" />
            <KpiCard label="Para revisar" value={s.flagged} foot="nota de 3 o menos" color="#B45309" />
          </>
        )}
      </Grid>

      <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="14px">
        <Flex px={5} py={4} justify="space-between" align="center" gap={3} wrap="wrap">
          <ChipGroup
            ariaLabel="Filtrar resultados"
            value={filter}
            onChange={(f) => { setFilter(f); setShown(PAGE); }}
            options={[
              { value: 'all', label: `Todas · ${data.counts.all}` },
              ...(comparing ? [] : [{ value: 'review', label: `Para revisar · ${data.counts.review}` }]),
              { value: 'teacher', label: `Al docente · ${data.counts.teacher}` },
            ]}
          />
          {canPublish && <Text fontSize="13px" color="gray.500">{selected.size} seleccionadas para publicar</Text>}
        </Flex>

        <Grid templateColumns={cols} px={5} py={3} bg="gray.50" fontSize="11px" fontWeight="600" letterSpacing=".06em" color="gray.600" textTransform="uppercase">
          {!comparing && <Text />}
          <Text>Entrega</Text><Text>{comparing ? 'IA / docente' : 'Nota'}</Text><Text>Retroalimentación</Text>{!comparing && <Text>Resultado</Text>}
        </Grid>

        {items.slice(0, shown).map((r) => {
          const res = resultOf(r, comparing);
          const diff = comparing && r.score !== null && r.teacherScore !== null ? r.score - r.teacherScore : null;
          return (
            <Grid key={r.sittingId} templateColumns={cols} px={5} py={3} alignItems="center" borderTop="1px solid" borderColor="gray.100" bg={r.flag && !comparing ? '#FFFBEB' : 'white'}>
              {!comparing && (
                <Checkbox
                  colorScheme="purple" aria-label={`Publicar ${r.activity}`} isDisabled={!r.publishable || !canPublish}
                  isChecked={selected.has(r.sittingId)} onChange={(e) => toggle(r.sittingId, e.target.checked)}
                />
              )}
              <Box minW={0} pr={3}>
                <Text fontSize="14px" fontWeight="600" isTruncated>{r.activity || `Entrega #${r.sittingId}`}</Text>
                <Text fontSize="12px" color="gray.500" isTruncated>{[r.student, r.subject, dayjs(r.createdAt).format('DD MMM')].filter(Boolean).join(' · ')}</Text>
              </Box>
              <Text fontSize="15px" fontWeight="700">
                {comparing
                  ? `${formatScore(r.score)} / ${formatScore(r.teacherScore)}`
                  : r.outcome === 'graded' ? formatScore(r.score) : '—'}
                {diff !== null && Math.abs(diff) > 2 && <Text as="span" fontSize="11px" color="#B45309" ml={1.5}>({diff > 0 ? '+' : ''}{formatScore(diff)})</Text>}
              </Text>
              <Text fontSize="13px" color="gray.600" lineHeight="1.4" pr={4} noOfLines={3}>
                {r.outcome === 'graded' ? r.feedback : reasonLabel(r.reason) || '—'}
              </Text>
              {!comparing && <Box justifySelf="start"><StatusPill label={res.label} tone={res.tone} /></Box>}
            </Grid>
          );
        })}
        {items.length > shown && (
          <Flex justify="center" py={3} borderTop="1px solid" borderColor="gray.100">
            <Button size="sm" variant="ghost" onClick={() => setShown(shown + PAGE)}>Ver {Math.min(PAGE, items.length - shown)} más de {items.length}</Button>
          </Flex>
        )}
      </Box>

      {data.phase !== 'running' && data.phase !== 'discarded' && data.phase !== 'failed' && (
        <Flex mt={4} px={5} py={4} borderRadius="12px" bg="#1E1B4B" color="white" align="center" justify="space-between" gap={4} wrap="wrap">
          <Box>
            {canPublish ? (
              <>
                <Text fontSize="14px" fontWeight="600">Publicar {selected.size} {selected.size === 1 ? 'nota' : 'notas'}</Text>
                <Text fontSize="13px" color="#C7D2FE">Se reparten en las siguientes ejecuciones de publicación (7:00 a 21:00). Las demás siguen con el docente.</Text>
              </>
            ) : (
              <Text fontSize="14px" color="#C7D2FE">Las pruebas de comparación no se publican.</Text>
            )}
          </Box>
          <Flex gap={2.5}>
            <Button variant="outline" color="white" borderColor="rgba(255,255,255,.4)" _hover={{ bg: 'rgba(255,255,255,.08)' }} borderRadius="10px" onClick={() => setModal('discard')}>
              Descartar prueba
            </Button>
            {canPublish && (
              <Button bg="white" color="#3730A3" fontWeight="700" borderRadius="10px" isDisabled={!selected.size} onClick={() => setModal('publish')}>
                Publicar {selected.size}
              </Button>
            )}
          </Flex>
        </Flex>
      )}

      <ConfirmActionModal
        isOpen={modal === 'publish'}
        onClose={() => setModal(null)}
        title={`Publicar ${selected.size} ${selected.size === 1 ? 'nota' : 'notas'}`}
        description="El estudiante recibe la nota y la retroalimentación en las siguientes ejecuciones de publicación, como si la hubiera calificado el docente. Mientras no se publiquen, puedes retenerlas desde Entregas."
        confirmLabel="Publicar"
        withReason={false}
        successMessage={(r) => `${r.published} ${r.published === 1 ? 'nota programada' : 'notas programadas'}${r.skipped ? ` · ${r.skipped} ya tenían nota` : ''}`}
        onConfirm={() => publishTest(testId, [...selected])}
        onDone={() => { load(); if (onChanged) onChanged(); }}
      />
      <ConfirmActionModal
        isOpen={modal === 'discard'}
        onClose={() => setModal(null)}
        title="Descartar prueba"
        description="La prueba queda archivada y no se publica nada más de ella. Lo que ya se aprobó sigue su curso."
        confirmLabel="Descartar"
        danger
        withReason={false}
        successMessage="Prueba descartada"
        onConfirm={() => discardTest(testId)}
        onDone={() => { load(); if (onChanged) onChanged(); }}
      />
    </Box>
  );
}
